import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, RotateCcw, ChevronLeft, AlertCircle, Calendar, Clock, CheckCircle } from 'lucide-react';
import { useAuthenticatedApi } from '../hooks/useAuthenticatedApi';

interface DeletedWorkspace {
  id: string;
  name: string;
  directory_path: string;
  created_at: string;
  updated_at: string;
  deleted: boolean;
  deleted_at: string;
  scheduled_hard_delete_at: string;
}

const DeletedProjectsPage: React.FC = () => {
  const navigate = useNavigate();
  const api = useAuthenticatedApi();
  
  const [deletedWorkspaces, setDeletedWorkspaces] = useState<DeletedWorkspace[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    loadDeletedWorkspaces();
  }, []);

  const loadDeletedWorkspaces = async () => {
    try {
      setLoading(true);
      const data = await api.get('/workspaces/deleted/list');
      setDeletedWorkspaces(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load deleted projects');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (workspaceId: string) => {
    try {
      setRestoringId(workspaceId);
      setError(null);
      await api.post(`/workspaces/${workspaceId}/restore`, {});
      
      // Show success message
      setSuccessMessage('Project restored successfully!');
      window.setTimeout(() => setSuccessMessage(null), 5000);
      
      // Reload the list
      await loadDeletedWorkspaces();
    } catch (err: any) {
      setError(err.message || 'Failed to restore project');
    } finally {
      setRestoringId(null);
    }
  };

  const getDaysUntilDeletion = (scheduledDeleteAt: string) => {
    const deleteDate = new Date(scheduledDeleteAt);
    const now = new Date();
    const diffTime = deleteDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-white dark:bg-neutral-950">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
      </div>
    );
  }

  return (
    <div className="h-full bg-white dark:bg-neutral-950 text-neutral-900 dark:text-white overflow-y-auto">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <button
            onClick={() => navigate('/select-project')}
            className="inline-flex items-center text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 mb-3 sm:mb-4 transition-colors"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to Projects
          </button>
          
          <div className="flex items-center">
            <Trash2 className="w-6 h-6 sm:w-8 sm:h-8 text-neutral-600 dark:text-neutral-400 mr-2 sm:mr-3" />
            <h1 className="text-2xl sm:text-3xl font-bold text-neutral-900 dark:text-white">Deleted Projects</h1>
          </div>
          <p className="mt-2 text-sm sm:text-base text-neutral-600 dark:text-neutral-400">
            Projects marked for deletion. You can restore them within 30 days.
          </p>
        </div>

        {error && (
          <div className="bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
            <p className="text-sm sm:text-base text-red-700 dark:text-red-400">{error}</p>
          </div>
        )}

        {successMessage && (
          <div className="bg-green-100 dark:bg-green-900/20 border border-green-300 dark:border-green-700 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6 flex items-center">
            <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 dark:text-green-400 mr-2" />
            <p className="text-sm sm:text-base text-green-700 dark:text-green-400">{successMessage}</p>
          </div>
        )}

        {/* Deleted Projects List */}
        {deletedWorkspaces.length === 0 ? (
          <div className="bg-slate-100 dark:bg-neutral-900 rounded-2xl shadow-xl border border-slate-300 dark:border-neutral-800 p-6 sm:p-12 text-center">
            <Trash2 className="w-12 h-12 sm:w-16 sm:h-16 text-neutral-400 dark:text-neutral-600 mx-auto mb-3 sm:mb-4" />
            <h3 className="text-base sm:text-lg font-medium text-neutral-900 dark:text-white mb-2">No deleted projects</h3>
            <p className="text-sm sm:text-base text-neutral-600 dark:text-neutral-400">Projects you delete will appear here for 30 days before permanent deletion.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {deletedWorkspaces.map((workspace) => {
              const daysUntilDeletion = getDaysUntilDeletion(workspace.scheduled_hard_delete_at);
              const isUrgent = daysUntilDeletion <= 7;
              
              return (
                <div
                  key={workspace.id}
                  className="bg-slate-100 dark:bg-neutral-900 border border-slate-300 dark:border-neutral-800 rounded-2xl shadow-xl p-4 sm:p-6 hover:shadow-2xl transition-shadow"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="text-base sm:text-lg font-semibold text-neutral-900 dark:text-white">{workspace.name}</h3>
                      <p className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-400 mt-1 font-mono break-all">{workspace.directory_path}</p>
                      
                      <div className="flex flex-col sm:flex-row sm:items-center mt-3 space-y-2 sm:space-y-0 sm:space-x-4 text-xs sm:text-sm text-neutral-500 dark:text-neutral-500">
                        <div className="flex items-center">
                          <Calendar className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                          <span className="truncate">Deleted {formatDate(workspace.deleted_at)}</span>
                        </div>
                        
                        <div className={`flex items-center ${isUrgent ? 'text-red-600 dark:text-red-400 font-medium' : ''}`}>
                          <Clock className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                          <span className="truncate">
                            {daysUntilDeletion > 0 
                              ? `${daysUntilDeletion} days until permanent deletion`
                              : 'Scheduled for permanent deletion'
                            }
                          </span>
                        </div>
                      </div>
                      
                      {isUrgent && (
                        <div className="mt-3 flex items-center text-amber-600 dark:text-amber-400">
                          <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                          <span className="text-xs sm:text-sm font-medium">
                            This project will be permanently deleted soon
                          </span>
                        </div>
                      )}
                    </div>
                    
                    <button
                      onClick={() => handleRestore(workspace.id)}
                      disabled={restoringId === workspace.id}
                      className={`px-3 sm:px-4 py-2 rounded-xl text-white font-medium flex items-center justify-center transition-all text-sm sm:text-base min-w-0 ${
                        restoringId === workspace.id
                          ? 'bg-neutral-400 dark:bg-neutral-600 cursor-not-allowed'
                          : 'bg-green-600 hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-700 shadow-sm hover:shadow-md'
                      }`}
                    >
                      {restoringId === workspace.id ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-white mr-1 sm:mr-2"></div>
                          <span className="hidden sm:inline">Restoring...</span>
                          <span className="sm:hidden">...</span>
                        </>
                      ) : (
                        <>
                          <RotateCcw className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                          Restore
                        </>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeletedProjectsPage; 